import { _ } from "../polyglot/polyglot.js";
import { download } from "../helpers/uri-util.js";
import { eventManager } from "@foretagsplatsen/yaem";
import { default as globalAlertBus } from "../alerts/alertBus.js"; // eslint-disable-line import-x/no-named-default -- To distinguish between the global and the internal buses
import { removeUndefinedValues } from "../helpers/object-util.js";
import { router } from "@foretagsplatsen/widgetjs";
import ResponseHandlerChain from "./ResponseHandlerChain.js";
import serialize from "./serialize.js";

const successHttpStatus = /^2../;

/**
 * A middleware that modifies a request before a repository call.
 * Will be called by a repositoryBuilder with a request as first
 * argument and a `next()` function as second. A request handler is
 * free to modify the content of the request and must call `next()`
 * for the next request handler to be executed.
 *
 * @typedef RequestHandler
 * @type {function(object, function): Promise}
 */

/**
 * `Repository` provides an asynchronous interface for server-side
 * data. All request methods return a promise that resolves upon
 * success and rejects in case of network failure or server error.
 *
 * See http://martinfowler.com/eaaCatalog/repository.html for more
 * information about the pattern.
 *
 * @param {string} spec.route - String used to build the route used in
 *   requests made to the server
 * @param {object} [spec.model] - The model class to use to materialize
 *   the fetched data. It must have a `materialize()` method and can
 *   optionally have a `materializeAll()` method
 * @param {object} [spec.defaultFilters] - Values to rewrite the route arguments
 * @param {object} [spec.context] - Arbitrary data to pass to the materializer
 * @param {object} [spec.notifier] - An object responsible for
 *   notifying the user of request successes and failures. If not
 *   provided, monitor's notification system is used
 * @param {RequestHandler[]} requestHandlers - All request handlers
 *   will be called in order until the array ends or a handler doesn't
 *   call next()
 * @param {ResponseHandler[]} [responseHandlers] - All handlers will
 *   be tested in order until one matches the response
 */
export default class Repository {
	constructor({
		route,
		model: materializer,
		defaultFilters = {},
		context = {},
		alertBus = globalAlertBus,
		requestHandlers = [],
		responseHandlers = [],
	}) {
		this._materializer = materializer;
		this._defaultFilters = defaultFilters;
		this._context = context;
		this._alertBus = alertBus;

		this._requestHandlers = requestHandlers;
		this._responseHandlers = responseHandlers;

		if (!route) {
			throw new Error(
				"No route provided, cannot initialize the repository.",
			);
		}

		this._route = router.route({ pattern: route });
	}

	addToContext(context) {
		this._context = { ...this._context, ...context };
	}

	getContext() {
		return this._context;
	}

	/**
	 * Fetch a collection of objects from the route and return a
	 * `Promise`.
	 *
	 * @param {object} [filter] - Key/value pairs to finish building
	 * the route if necessary.
	 * @param {Renderable} [successMessage] - What to show in the
	 * notification area when the request completes
	 * successfully. Nothing is shown by default.
	 * @param {Renderable} [errorMessage] - What to show in the
	 * notification area when the request fails. A general-purpose
	 * error message is shown by default.
	 * @param {boolean} [notify] - When false, ignore the values of
	 * `successMessage` and `errorMessage` and don't notify on success
	 * or failure.
	 * @param {object} [context] - Arbitrary data to pass to the
	 * materializer. If provided, the value takes precedence over the
	 * `context` value passed at repository-construction time.
	 * @return {Promise}
	 */
	get({
		filter,
		successMessage,
		errorMessage = _("Failed to load data"),
		notify = true,
		context,
	} = {}) {
		return this._query("GET", {
			filter,
			successMessage,
			errorMessage,
			notify,
			context,
		});
	}

	/**
	 * Ask the server to save `model` at the given route and return a
	 * `Promise`.
	 *
	 * @param {object} model - Anything that can be serialized and
	 * sent to the server.
	 * @param {object} [filter] - Key/value pairs to finish building
	 * the route if necessary.
	 * @param {Renderable} [successMessage] - What to show in the
	 * notification area when the request completes successfully. A
	 * general-purpose error message is shown by default.
	 * @param {Renderable} [errorMessage] - What to show in the
	 * notification area when the request fails. A general-purpose
	 * error message is shown by default.
	 * @param {boolean} [notify] - When false, ignore the values of
	 * `successMessage` and `errorMessage` and don't notify on success
	 * or failure.
	 * @param {object} [context] - Arbitrary data to pass to the
	 * materializer. If provided, the value takes precedence over the
	 * `context` value passed at repository-construction time.
	 * @param {boolean} [readResponse] - Whether to materialize and return
	 * the response sent by the server.
	 * @return {Promise}
	 */
	save({
		model,
		filter,
		successMessage = _("Save completed successfully"),
		errorMessage = _("Save failed"),
		notify = true,
		context,
		readResponse = true,
	} = {}) {
		return this._query("POST", {
			filter,
			model,
			successMessage,
			errorMessage,
			notify,
			context,
			readResponse,
		});
	}

	/**
	 * Ask the server to replace the entity at the given route by
	 * `model` and return a `Promise`.
	 *
	 * @param {object} model - Anything that can be serialized and
	 * sent to the server.
	 * @param {object} [filter] - Key/value pairs to finish building
	 * the route if necessary.
	 * @param {Renderable} [successMessage] - What to show in the
	 * notification area when the request completes successfully. A
	 * general-purpose error message is shown by default.
	 * @param {Renderable} [errorMessage] - What to show in the
	 * notification area when the request fails. A general-purpose
	 * error message is shown by default.
	 * @param {boolean} [notify] - When false, ignore the values of
	 * `successMessage` and `errorMessage` and don't notify on success
	 * or failure.
	 * @param {object} [context] - Arbitrary data to pass to the
	 * materializer. If provided, the value takes precedence over the
	 * `context` value passed at repository-construction time.
	 * @param {boolean} [readResponse] - Whether to materialize and return
	 * the response sent by the server.
	 * @return {Promise}
	 */
	update({
		model,
		filter,
		successMessage = _("Update completed successfully"),
		errorMessage = _("Update failed"),
		notify = true,
		context,
		readResponse = true,
	} = {}) {
		return this._query("PUT", {
			filter,
			model,
			successMessage,
			errorMessage,
			notify,
			context,
			readResponse,
		});
	}

	/**
	 * Ask the server to delete the entity at the given route and return a
	 * `Promise`.
	 *
	 * @param {object} [filter] - Key/value pairs to finish building
	 * the route if necessary.
	 * @param {Renderable} [successMessage] - What to show in the
	 * notification area when the request completes successfully. A
	 * general-purpose error message is shown by default.
	 * @param {Renderable} [errorMessage] - What to show in the
	 * notification area when the request fails. A general-purpose
	 * error message is shown by default.
	 * @param {boolean} [notify] - When false, ignore the values of
	 * `successMessage` and `errorMessage` and don't notify on success
	 * or failure.
	 * @param {object} [context] - Arbitrary data to pass to the
	 * materializer. If provided, the value takes precedence over the
	 * `context` value passed at repository-construction time.
	 * @param {boolean} [readResponse] - Whether to materialize and return
	 * the response sent by the server.
	 * @return {Promise}
	 */
	remove({
		filter,
		successMessage = _("Remove completed successfully"),
		errorMessage = _("Removal failed"),
		notify = true,
		context,
		readResponse = false,
	} = {}) {
		return this._query("DELETE", {
			filter,
			successMessage,
			errorMessage,
			notify,
			context,
			readResponse,
		});
	}

	/**
	 * Return a `Promise` resolving to `true` if the requested
	 * resource exists and `false` if it doesn't. The promise is
	 * rejected in case of failure.
	 *
	 * @param {object} [filter] - Key/value pairs to finish building
	 * the route if necessary.
	 * @return {Promise}
	 */
	head({ filter } = {}) {
		return this._query("HEAD", {
			filter,
			responseHandlers: [
				{ test: successHttpStatus, handle: () => true },
				{ test: 404, handle: () => false },
			],
		});
	}

	download({
		filter,
		filename,
		downloader = { download },
		errorMessage = _("Failed to download"),
		notify = true,
	} = {}) {
		return this._query("GET", {
			filter,
			errorMessage,
			notify,
			responseHandlers: [
				{
					test: successHttpStatus,
					handle: async ({ response }) => {
						const data = await response.text();
						const mimetype = response.headers.get("Content-Type");

						downloader.download({ data, mimetype, filename });
					},
				},
			],
		});
	}

	/**
	 * Return a string reprenting the repository's route URL expanded
	 * with `filter` and the repository's `defaultFilters`.
	 *
	 * @param {object} [filter] - Key/value pairs to finish building
	 * the route if necessary.
	 * @return {string} A fully-expanded URL.
	 */
	buildUrl({ filter = {} } = {}) {
		const allFilters = {
			...this._defaultFilters,
			...filter,
		};

		removeUndefinedValues(allFilters);

		return this._route.expand(allFilters);
	}

	async _query(method, querySpec) {
		const {
			model,
			filter,
			notify,
			context,
			readResponse = true,
			responseHandlers = [],
		} = querySpec;

		let { successMessage, errorMessage } = querySpec;

		if (!notify) {
			successMessage = null;
			errorMessage = null;
		}

		const url = `/${this.buildUrl({ filter })}`;
		const response = await this._fetch({ url, method, model });

		const responseContext = {
			response,
			context: context || this._context,
			shouldReadResponse: readResponse,
			successMessage,
			errorMessage,
			alertBus: this._alertBus,
			materializer: this._materializer,
			request: (newSpec) =>
				this._query(method, { ...querySpec, ...newSpec }),
		};

		const result = await new ResponseHandlerChain({
			responseHandlers: [...responseHandlers, ...this._responseHandlers],
		}).handle(responseContext);

		this._triggerLoadedEvent();

		return result;
	}

	async _fetch({ url, method, model }) {
		const fetchConfiguration = this._generateFetchConfiguration({
			method,
			model,
		});

		this._triggerLoadingEvent();
		const request = { url, fetchConfiguration };
		await this._executeRequestHandlers(request);

		return fetch(request.url, request.fetchConfiguration);
	}

	async _executeRequestHandlers(request) {
		let shouldContinue = true;

		let handlerIndex = 0;
		const next = () => (shouldContinue = true);

		while (shouldContinue && handlerIndex < this._requestHandlers.length) {
			// The next request handler will only execute if the
			// current one calls next():
			shouldContinue = false;

			// eslint-disable-next-line no-await-in-loop -- request handlers must be executed in sequence
			await this._requestHandlers[handlerIndex](request, next);

			handlerIndex++;
		}
	}

	_generateFetchConfiguration({ method, model }) {
		const isDelete = method === "DELETE";

		const defaultAccept = isDelete ? "text/plain" : "application/json";

		const defaultContentType = isDelete
			? "text/plain"
			: "application/json;charset=utf-8";

		return {
			method,
			body: serialize(model),
			credentials: "same-origin",
			headers: {
				Accept: defaultAccept,
				"Content-Type": defaultContentType,
			},
		};
	}

	_triggerLoadingEvent() {
		eventManager.at("sync").trigger("loading", event);
	}

	_triggerLoadedEvent() {
		eventManager.at("sync").trigger("loaded", event);
	}
}
